import { Button, Text, VSpacer } from '@/components/DesignSystem';
import ERPRetailerSelector from '@/components/shared/ListSelector/ERPRetailerSelector';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { BillingExportDialog } from '@/pages/Reports/BillingExportDialog';
import { ReportHistory } from '@/pages/Reports/ReportHistory';
import { ExportOnlyReports } from '@/pages/Reports/reportRouting';
import {
  Container,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import { hasUserPermission, ReportType, UserPermission } from '@shared/enums';
import { ApiRetailer } from '@api/interfaces';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

type ReportTypeKey = keyof typeof import('@/locales/en.json')['reports']['reportType'];

type Report = {
  translationKey: ReportTypeKey,
} & ({ reportType: ReportType, route?: never } | { reportType?: never, route: Routes });

export const Reports = () => {
  const { isInternal, retailer, user } = useAuthentication();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pickedRetailerId, setPickedRetailerId] = useState<string>();
  const [exportingReportType, setExportingReportType] = useState<ReportType>();

  const selectedRetailerId = isInternal
    ? pickedRetailerId ?? searchParams.get('retailerId') ?? undefined
    : retailer?.id;

  const canGenerateReports = isInternal || hasUserPermission(
    user?.permissions, UserPermission.CanAccessReports,
  );
  const [tab, setTab] = useState<'generate' | 'history'>(
    canGenerateReports ? 'generate' : 'history',
  );
  const reports: Report[] = [
    { reportType: ReportType.CustomerPoints, translationKey: 'customerPoints' },
    { reportType: ReportType.TransactionPointsHistory, translationKey: 'transactionPointsHistory' },
  ];

  if (!retailer?.isProgramCreationDisabled) {
    reports.push({ reportType: ReportType.TierProgress, translationKey: 'tierProgress' });
    reports.push({ reportType: ReportType.CustomerOverview, translationKey: 'customerOverview' });
  }

  if (isInternal) {
    reports.push({ reportType: ReportType.Billing, translationKey: 'billing' });
    reports.push({ route: Routes.RETAILER_OVERVIEW, translationKey: 'overview' });
  }

  const retailerSearch = selectedRetailerId ? `?retailerId=${selectedRetailerId}` : '';
  const openReport = ({ reportType, route }: Report) => {
    if (route) {
      navigate({ pathname: route, search: retailerSearch });
    } else if (ExportOnlyReports.includes(reportType)) {
      setExportingReportType(reportType);
    } else {
      navigate({
        pathname: Routes.REPORT_PAGE.replace(':reportType', reportType),
        search: retailerSearch,
      });
    }
  };
  const getButtonLabel = ({ reportType, route }: Report) => {
    if (route) {
      return t($ => $.reports.view);
    }
    return t($ => (ExportOnlyReports.includes(reportType)
      ? $.common.actions.export
      : $.reports.viewReport));
  };

  return (
    <Container maxWidth="lg">
      {isInternal
        ? (
          <ERPRetailerSelector
            header={t($ => $.reports.title)}
            onSelect={(selected?: ApiRetailer) => setPickedRetailerId(selected?.id)}
          />
        )
        : <Text category="display-small">{t($ => $.reports.title)}</Text>}
      <VSpacer size="5" />
      <Tabs onChange={(_, value) => setTab(value)} value={tab}>
        {canGenerateReports && (
          <Tab
            data-testid="reports-generate-tab"
            label={t($ => $.reports.generateTab)}
            value="generate"
          />
        )}
        <Tab
          data-testid="reports-history-tab"
          label={t($ => $.reports.history.tab)}
          value="history"
        />
      </Tabs>
      <VSpacer size="5" />
      {tab === 'history' && (
        <ReportHistory key={selectedRetailerId} retailerId={selectedRetailerId} />
      )}
      {tab === 'generate' && (
      <Table size="small" stickyHeader>
        <TableHead
          sx={{
            '& .MuiTableCell-root.MuiTableCell-head': {
              backgroundColor: theme => theme.palette.background.paper,
              borderBottom: theme => `1px solid ${theme.palette.outline}`,
            },
          }}
        >
          <TableRow sx={{ height: '56px' }}>
            <TableCell width={220}>
              <Text category="label-large">{t($ => $.reports.reportName)}</Text>
            </TableCell>
            <TableCell>
              <Text category="label-large">{t($ => $.reports.reportDescription)}</Text>
            </TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {reports.map((report) => (
            <TableRow
              key={report.translationKey}
              sx={{ '& td': { paddingTop: '12px', paddingBottom: '12px' } }}
            >
              <TableCell>
                <Text category="title-small">
                  {t($ => $.reports.reportType[report.translationKey].name)}
                </Text>
              </TableCell>
              <TableCell>
                <Text category="body-medium">
                  {t($ => $.reports.reportType[report.translationKey].description)}
                </Text>
              </TableCell>
              <TableCell align="right">
                <Button
                  disabled={!selectedRetailerId}
                  onClick={() => openReport(report)}
                  sx={{ borderRadius: '4px' }}
                  testID={`generate-${report.translationKey}-report-button`}
                >
                  {getButtonLabel(report)}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
      {exportingReportType === ReportType.Billing && !!selectedRetailerId && (
        <BillingExportDialog
          onClose={() => setExportingReportType(undefined)}
          retailerId={selectedRetailerId}
        />
      )}
    </Container>
  );
};
